import { Dialog, DialogDismiss, DialogHeading, Focusable } from '@ariakit/react';
import { useCallback } from 'react';
import { motion } from 'motion/react';

import { CircularCloseIcon, typography } from '@ui';

import * as css from './TagSelection.css';
import { useForm } from 'react-hook-form';

type TagModalProps = {
    tagId: number;
    tagName?: string;
    onClose: () => void;
    onSave: (tagId: number, name: string) => void;
    onDelete: (tagId: number) => void;
};

type TagNameForm = {
    name: string;
};

export const TagModal = ({ tagId, tagName, onClose, onDelete, onSave }: TagModalProps) => {
    const { register, handleSubmit } = useForm<TagNameForm>({
        defaultValues: {
            name: tagName || '',
        },
    });

    const handleDeleteClick = useCallback(() => onDelete(tagId), [onDelete, tagId]);
    const handleSave = useCallback(({ name }: TagNameForm) => onSave(tagId, name), [onSave, tagId]);
    const isEdit = tagId > 0;

    return (
        <Dialog
            open
            onClose={onClose}
            className={css.modal}
            backdrop={
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                        backgroundColor: 'rgb(0,0,0,0.3)',
                    }}
                />
            }
            render={
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                />
            }
        >
            <form onSubmit={handleSubmit(handleSave)} className={css.modalForm}>
                <div className={css.modalTop}>
                    <DialogHeading
                        className={typography({
                            variant: 'h3',
                            weight: 'bold',
                        })}
                    >
                        {isEdit ? 'Edit Tag' : 'Add Tag'}
                    </DialogHeading>
                    <DialogDismiss>
                        <CircularCloseIcon width={24} />
                    </DialogDismiss>
                </div>
                <Focusable
                    autoFocus
                    render={<input className={css.modalInput} {...register('name', { required: true })} />}
                />
                <div className={css.modalButtons}>
                    {isEdit && (
                        <DialogDismiss
                            type="button"
                            onClick={handleDeleteClick}
                            className={css.modalButton({ variant: 'delete' })}
                        >
                            Delete
                        </DialogDismiss>
                    )}
                    <DialogDismiss type="submit" className={css.modalButton({ variant: 'save' })}>
                        {isEdit ? 'Save' : 'Add'}
                    </DialogDismiss>
                </div>
            </form>
        </Dialog>
    );
};
